import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { useAcceptInvitation } from "../api";
import { apiErrorDetail } from "../lib/errors";
import { GroupRole } from "../types";

/**
 * Invitation acceptance panel. The route tree is frozen, so acceptance is
 * deep-linked into the partners area via `?accept=<token>` (mirrors the
 * communications slice's `?review=` convention). The invitee must already be
 * authenticated — the API's accept endpoint operates on `request.user`.
 *
 * A member invitation is not accepted by token: an existing account answers it
 * from the dashboard notice. The API refuses it with `answer_from_dashboard`,
 * and the panel sends the invitee there — mirroring the legacy
 * AcceptInvitationView, which redirects to the home page.
 */
export function InvitationAccept({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const accept = useAcceptInvitation();
  const navigate = useNavigate();

  const answerFromDashboard =
    (accept.error as { code?: string } | null)?.code === "answer_from_dashboard";
  useEffect(() => {
    if (answerFromDashboard) void navigate({ to: "/dashboard", replace: true });
  }, [answerFromDashboard, navigate]);

  const accepted = accept.isSuccess;
  const errorDetail = accept.isError
    ? (apiErrorDetail(accept.error) ?? t("partners.accept.error"))
    : null;

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle>{t("partners.accept.heading")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {accepted ? (
          <div className="space-y-4">
            <p className="flex items-center gap-2 text-success">
              <CheckCircle2 className="size-5" aria-hidden />
              {accept.data?.group_role === GroupRole.LocalPartner
                ? t("dashboard.invitations.joinedLocalPartner")
                : t("partners.accept.success")}
            </p>
            <p className="text-sm text-muted-foreground">{t("partners.accept.done")}</p>
            <Button onClick={onDone}>{t("partners.detail.back")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-muted-foreground">{t("partners.accept.body")}</p>
            {errorDetail && <p className="text-sm text-destructive">{errorDetail}</p>}
            <div className="flex items-center gap-2">
              <Button
                disabled={accept.isPending}
                onClick={() => accept.mutate(token)}
              >
                {accept.isPending
                  ? t("partners.accept.accepting")
                  : t("partners.accept.accept")}
              </Button>
              <Button variant="ghost" onClick={onDone}>
                {t("partners.invite.cancel")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
